/* FinMaster landing page behaviour: carousels, click-to-play video, mobile menu. */

class FmCarousel extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-fm-slide]')];
    this.dots = [...this.querySelectorAll('[data-fm-dot]')];
    this.prev = this.querySelector('[data-fm-prev]');
    this.next = this.querySelector('[data-fm-next]');
    this.loop = this.hasAttribute('loop');
    const start = this.slides.findIndex((slide) => !slide.hidden);
    this.index = start < 0 ? 0 : start;

    this.prev?.addEventListener('click', () => this.go(this.index - 1, 'prev'));
    this.next?.addEventListener('click', () => this.go(this.index + 1, 'next'));
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i)));
    this.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') this.go(this.index - 1, 'prev');
      if (event.key === 'ArrowRight') this.go(this.index + 1, 'next');
    });
    this.addSwipe();
    this.update(false);
  }

  // With [loop], stepping past either end wraps around; the new slide always enters from the direction of travel,
  // so there is never a visible rewind to the first item.
  go(index, dir) {
    const count = this.slides.length;
    if (count < 2) return;
    let target = index;
    if (this.loop) target = (index + count) % count;
    if (target < 0 || target >= count || target === this.index) return;
    this.direction = dir || (target > this.index ? 'next' : 'prev');
    this.index = target;
    this.update(true);
  }

  update(animate) {
    const count = this.slides.length;
    this.slides.forEach((slide, i) => {
      const active = i === this.index;
      slide.hidden = !active;
      slide.classList.remove('is-entering');
      if (active && animate) {
        slide.dataset.dir = this.direction;
        void slide.offsetWidth; // restart the entrance animation
        slide.classList.add('is-entering');
      }
    });
    this.dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === this.index)));
    if (!this.loop) {
      this.prev?.setAttribute('aria-disabled', String(this.index === 0 || count < 2));
      this.next?.setAttribute('aria-disabled', String(this.index === count - 1 || count < 2));
    }
    const live = this.querySelector('[data-fm-live]');
    if (live && animate) live.textContent = `${this.index + 1} / ${count}`;
    // artwork inside the new slide had no layout while hidden; let the parallax layer re-measure it
    if (animate) this.dispatchEvent(new CustomEvent('fm:slidechange', { bubbles: true }));
  }

  addSwipe() {
    let startX = null;
    let startY = null;
    let swiped = false;
    this.addEventListener('pointerdown', (event) => {
      startX = event.clientX;
      startY = event.clientY;
      swiped = false;
    }, { passive: true });
    this.addEventListener('pointerup', (event) => {
      if (startX === null) return;
      const dx = event.clientX - startX;
      const dy = event.clientY - startY;
      startX = null;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
        swiped = true;
        this.go(this.index + (dx < 0 ? 1 : -1), dx < 0 ? 'next' : 'prev');
      }
    }, { passive: true });
    // a swipe across a card must not also "click" it (e.g. open the review video)
    this.addEventListener('click', (event) => {
      if (!swiped) return;
      swiped = false;
      event.preventDefault();
      event.stopPropagation();
    }, true);
  }
}

class FmVideo extends HTMLElement {
  connectedCallback() {
    const play = this.querySelector('[data-fm-play]');
    if (!play) return;
    play.addEventListener('click', () => this.play());
    // warm up the video host only when the visitor shows intent (nothing is loaded before that)
    const warm = () => this.preconnect();
    play.addEventListener('pointerenter', warm, { once: true });
    play.addEventListener('focus', warm, { once: true });
  }

  preconnect() {
    if (this.warmed) return;
    this.warmed = true;
    (this.dataset.preconnect || '').split(' ').filter(Boolean).forEach((href) => {
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = href;
      link.crossOrigin = '';
      document.head.append(link);
    });
  }

  play() {
    const template = this.querySelector('template');
    const frame = this.querySelector('.fm-video__frame');
    if (!template || !frame || frame.classList.contains('is-playing')) return;
    this.preconnect();
    frame.append(template.content.cloneNode(true));
    const media = frame.querySelector('iframe, video');
    const reveal = () => frame.classList.add('is-playing');
    if (media?.tagName === 'VIDEO') {
      media.addEventListener('loadeddata', reveal, { once: true });
      media.play?.().catch(() => {});
    } else {
      media?.addEventListener('load', reveal, { once: true });
    }
    setTimeout(reveal, 2500); // never leave the poster up if the load event is missed
    media?.focus({ preventScroll: true });
  }
}

/* Mobile menu (native <dialog>). Toggles live in the hero bar and in the sticky header. */
class FmMenu extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-fm-menu]');
    if (!this.dialog) return;
    const toggles = () => document.querySelectorAll(`[data-fm-menu-open][aria-controls="${this.dialog.id}"]`);
    const setExpanded = (open) => toggles().forEach((t) => t.setAttribute('aria-expanded', String(open)));
    this.opener = null;
    document.addEventListener('click', (event) => {
      const toggle = event.target.closest?.('[data-fm-menu-open]');
      if (!toggle || toggle.getAttribute('aria-controls') !== this.dialog.id) return;
      this.opener = toggle;
      this.dialog.showModal();
      setExpanded(true);
    });
    this.querySelector('[data-fm-menu-close]')?.addEventListener('click', () => this.dialog.close());
    this.dialog.addEventListener('click', (event) => {
      if (event.target.closest('a') || event.target === this.dialog) this.dialog.close();
    });
    this.dialog.addEventListener('close', () => {
      setExpanded(false);
      if (this.opener?.isConnected && !this.opener.closest('[inert]')) this.opener.focus({ preventScroll: true });
    });
  }
}

/* Review video modal: the Shopify video element is created from the card's <template> only when the visitor
   opens it, plays after that click, and is paused and removed again when the modal closes. */
function fmInitReviewModal() {
  document.addEventListener('click', (event) => {
    const card = event.target.closest?.('[data-fm-watch]');
    if (!card) return;
    const modal = document.getElementById(card.getAttribute('aria-controls'));
    const template = card.parentElement.querySelector('template[data-fm-video]');
    const frame = modal?.querySelector('[data-fm-modal-frame]');
    if (!modal || !template || !frame) return;
    frame.replaceChildren(template.content.cloneNode(true));
    modal.style.setProperty('--ar', template.dataset.ar || 0.5625);
    modal.fmOpener = card;
    modal.showModal();
    const video = frame.querySelector('video');
    video?.play?.().catch(() => {});
  });

  document.querySelectorAll('[data-fm-review-modal]').forEach((modal) => {
    modal.querySelector('[data-fm-modal-close]')?.addEventListener('click', () => modal.close());
    modal.addEventListener('click', (event) => {
      if (event.target === modal) modal.close(); // backdrop
    });
    modal.addEventListener('close', () => {
      const frame = modal.querySelector('[data-fm-modal-frame]');
      frame.querySelector('video')?.pause();
      frame.replaceChildren(); // stop any further download
      // return focus to the card that opened it (Safari does not focus buttons on click)
      if (modal.fmOpener?.isConnected) modal.fmOpener.focus({ preventScroll: true });
    });
  });
}

/*
 * FinMaster header (snippets/fm-header.liquid): the homepage switches from full to compact on scroll; internal
 * pages start compact. On pages with a header space (no hero) the header follows that space to the top.
 */
function fmInitHeader() {
  const header = document.querySelector('[data-fm-header]');
  if (!header) return;
  const space = document.querySelector('[data-fm-header-space]');
  const wrapper = document.querySelector('.page-wrapper');
  // the theme scrolls .page-wrapper on desktop and the document on mobile
  const scroller = () =>
    wrapper && wrapper.scrollHeight > wrapper.clientHeight + 1 && getComputedStyle(wrapper).overflowY !== 'visible'
      ? wrapper
      : document.scrollingElement || document.documentElement;
  let ticking = false;
  const update = () => {
    ticking = false;
    header.classList.toggle('is-compact', header.classList.contains('fm-header--page') || scroller().scrollTop > 16);
    if (space) {
      const top = Math.max(0, Math.round(space.getBoundingClientRect().top));
      header.style.top = top ? `${top}px` : '';
    }
  };
  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(update);
  };
  // keep the fixed header clear of the theme's desktop scrollbar (.page-wrapper scrolls, not the window)
  const syncScrollbar = () => {
    const sbw = wrapper && wrapper.scrollHeight > wrapper.clientHeight ? wrapper.offsetWidth - wrapper.clientWidth : 0;
    header.style.setProperty('--fm-sbw', `${sbw}px`);
    onScroll();
  };
  update();
  syncScrollbar();
  requestAnimationFrame(() => header.classList.add('is-ready')); // transitions only after the first state
  document.addEventListener('scroll', onScroll, { passive: true, capture: true });
  window.addEventListener('resize', syncScrollbar, { passive: true });
}

/* Buy FinMaster: add to the Shopify cart with the Cart AJAX API, update every cart count, stay on the page. */
const FmCart = (() => {
  const status = () => document.querySelector('[data-fm-cart-status]');

  function render(count) {
    document.querySelectorAll('[data-fm-cart]').forEach((cart) => {
      const badge = cart.querySelector('[data-fm-cart-count]');
      const label = cart.querySelector('[data-fm-cart-label]');
      if (badge) {
        badge.textContent = count > 99 ? '99+' : String(count);
        badge.hidden = count === 0;
      }
      if (label) label.textContent = `Cart, ${count} ${count === 1 ? 'item' : 'items'}`;
    });
  }

  function bump() {
    document.querySelectorAll('[data-fm-cart]').forEach((cart) => {
      cart.classList.remove('is-bump');
      void cart.offsetWidth; // restart the animation
      cart.classList.add('is-bump');
    });
  }

  async function refresh(cartUrl = '/cart') {
    try {
      const res = await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } });
      if (!res.ok) return null;
      const cart = await res.json();
      render(cart.item_count);
      return cart;
    } catch {
      return null;
    }
  }

  function feedback(button, text, className, ms) {
    const label = button.querySelector('[data-fm-btn-label]');
    if (!label) return;
    clearTimeout(button.fmTimer);
    button.dataset.fmLabel ??= label.textContent;
    label.textContent = text;
    button.classList.remove('is-added');
    void button.offsetWidth;
    if (className) button.classList.add(className);
    button.fmTimer = setTimeout(() => {
      label.textContent = button.dataset.fmLabel;
      button.classList.remove('is-added');
    }, ms);
  }

  // The theme's cart drawer (assets/cart-drawer.js, component-cart-items.js) listens for Shopify's standard
  // cart:lines-update event: it re-renders its items from the event's sections and can open itself.
  const events = () => import('@shopify/events').catch(() => null);
  const drawerSections = () =>
    [...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean);

  // Open once the drawer has rendered the new line (it re-renders from the add response), so an empty cart
  // never flashes "Your cart is empty" first. Gives up waiting after ~0.8s and opens anyway.
  function openDrawer() {
    const drawer = document.querySelector('cart-drawer-component')?.closest('theme-drawer');
    if (!drawer || drawer.isOpen) return;
    const start = performance.now();
    const tick = () => {
      const rendered = !drawer.querySelector('.cart-drawer--empty');
      if (rendered || performance.now() - start > 800) drawer.open?.();
      else requestAnimationFrame(tick);
    };
    tick();
  }

  async function add(form) {
    const button = form.querySelector('button[type="submit"]');
    if (!button || button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');
    button.classList.add('is-busy');
    const body = new FormData(form);
    const sections = drawerSections();
    if (sections.length) body.append('sections', sections.join(','));
    const std = await events();
    const deferred = std?.CartLinesUpdateEvent.createPromise();
    if (std) {
      const event = new std.CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(body.get('id')), quantity: Number(body.get('quantity')) || 1 }],
        promise: deferred.promise,
      });
      event.promise.catch(() => {}); // failures are reported on the button; listeners handle their own
      form.dispatchEvent(event);
    }
    try {
      const res = await fetch(`${form.getAttribute('action')}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.description || data.message || form.dataset.errorLabel);
      const cart = await refresh(form.dataset.cartUrl);
      if (std && cart) {
        deferred.resolve({
          cart: std.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { items: cart.items, sections: data.sections, source: 'fm-buy-button', itemCount: 1, productId: data.product_id, didError: false },
        });
      } else {
        deferred?.reject(new Error('cart refresh failed'));
      }
      feedback(button, form.dataset.addedLabel, 'is-added', 1800);
      bump();
      openDrawer();
      const count = cart?.item_count;
      const name = data.product_title || data.title || 'FinMaster';
      if (status()) status().textContent = `${name} added to cart.${count != null ? ` Cart has ${count} ${count === 1 ? 'item' : 'items'}.` : ''}`;
    } catch (error) {
      deferred?.reject(error);
      feedback(button, form.dataset.errorLabel, null, 2600);
      if (status()) status().textContent = error.message || form.dataset.errorLabel;
    } finally {
      button.removeAttribute('aria-busy');
      button.classList.remove('is-busy');
    }
  }

  function init() {
    document.addEventListener('submit', (event) => {
      const form = event.target.closest?.('form[data-fm-add]');
      if (!form || !window.fetch) return;
      event.preventDefault();
      add(form);
    });
    // back/forward cache: the count may have changed on the cart page
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) refresh(document.querySelector('form[data-fm-add]')?.dataset.cartUrl);
    });
    // quantity changes and removals in the theme's cart drawer: keep the FinMaster cart counts in step
    document.addEventListener('shopify:cart:lines-update', (event) => {
      if (event.target instanceof Element && event.target.closest('form[data-fm-add]')) return; // refreshed by add()
      event.promise?.then(() => refresh()).catch(() => {});
    });
  }

  return { init, refresh };
})();

/*
 * Motion layer. The static layout is the Figma design; motion is added on top and never required:
 * - [data-fm-reveal]      one-time scroll reveal (children of [data-fm-stagger] get staggered delays)
 * - [data-fm-parallax=n]  vertical drift of n px (design px) per viewport height of scroll, optional
 *   [data-fm-rotate=deg]  rotation over the same range. Written straight to the element's transform.
 * Disabled entirely for prefers-reduced-motion. Parallax is halved on small screens.
 */
const FmMotion = (() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const small = window.matchMedia('(max-width: 989px)');
  let items = [];
  let ticking = false;

  // Reveal when an element comes into view; reset it only once it has fully left the viewport, so it can
  // reveal again on re-entry but never re-triggers while it is still (partly) visible.
  const settleTimers = new WeakMap();
  let enterIO;
  let exitIO;

  function revealDelay(el) {
    const cs = getComputedStyle(el);
    return (parseFloat(cs.getPropertyValue('--i')) || 0) * 90 + (parseFloat(cs.getPropertyValue('--d')) || 0);
  }

  function show(el) {
    if (el.classList.contains('is-in')) return;
    el.classList.add('is-in');
    // once the entrance has finished, drop its delays so hover/tilt respond immediately
    settleTimers.set(el, setTimeout(() => el.classList.add('is-settled'), revealDelay(el) + 1150));
  }

  function reset(el) {
    clearTimeout(settleTimers.get(el));
    el.classList.remove('is-in', 'is-settled');
  }

  // How to Play entrance (finmaster.css): .is-in once the slider is well into view, removed when it has fully left.
  let stepsInIO;
  let stepsOutIO;
  function steps(root) {
    if (!('IntersectionObserver' in window)) return;
    stepsInIO ??= new IntersectionObserver(
      (entries) => entries.forEach((entry) => entry.isIntersecting && entry.target.classList.add('is-in')),
      { rootMargin: '0px 0px -15% 0px', threshold: 0.2 }
    );
    stepsOutIO ??= new IntersectionObserver(
      (entries) => entries.forEach((entry) => !entry.isIntersecting && entry.target.classList.remove('is-in')),
      { threshold: 0 }
    );
    root.querySelectorAll('[data-fm-steps]').forEach((el) => {
      stepsInIO.observe(el);
      stepsOutIO.observe(el);
    });
  }

  function reveal(root) {
    root.querySelectorAll('[data-fm-stagger]').forEach((group) => {
      [...group.querySelectorAll(':scope > [data-fm-reveal], :scope > * > [data-fm-reveal]')].forEach((el, i) => {
        el.style.setProperty('--i', i);
      });
    });
    const targets = root.querySelectorAll('[data-fm-reveal]');
    if (!('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('is-in', 'is-settled'));
      return;
    }
    enterIO ??= new IntersectionObserver(
      (entries) => entries.forEach((entry) => entry.isIntersecting && show(entry.target)),
      { rootMargin: '0px 0px -12% 0px', threshold: 0.08 }
    );
    exitIO ??= new IntersectionObserver(
      (entries) => entries.forEach((entry) => !entry.isIntersecting && reset(entry.target)),
      { rootMargin: '40px 0px 40px 0px', threshold: 0 }
    );
    targets.forEach((el) => {
      enterIO.observe(el);
      exitIO.observe(el);
    });
  }

  // The theme scrolls .page-wrapper on desktop (base.css) and the document on mobile.
  function scroller() {
    const wrapper = document.querySelector('.page-wrapper');
    if (wrapper && wrapper.scrollHeight > wrapper.clientHeight + 1 && getComputedStyle(wrapper).overflowY !== 'visible') return wrapper;
    return document.scrollingElement || document.documentElement;
  }

  // Cache each element's untransformed centre in scroll coordinates once, so the scroll handler only
  // reads scrollTop (no per-frame layout reads, and the parallax offset never feeds back into itself).
  function measure() {
    const factor = small.matches ? 0.5 : 1;
    const sc = scroller();
    const scrollTop = sc.scrollTop;
    items = [...document.querySelectorAll('[data-fm-parallax]')].map((el) => {
      const prev = el.style.transform;
      el.style.transform = 'none';
      const rect = el.getBoundingClientRect();
      el.style.transform = prev;
      el.style.removeProperty('--py');
      // same maths as --u in finmaster.css: one design px in screen px
      const width = el.closest('.fm')?.clientWidth || window.innerWidth;
      const unit = small.matches ? Math.min(width, 600) / 390 : Math.min(1, width / 1728);
      return {
        el,
        unit,
        center: rect.top + scrollTop + rect.height / 2,
        half: rect.height / 2,
        speed: parseFloat(el.dataset.fmParallax) * factor,
        rotate: parseFloat(el.dataset.fmRotate || 0) * factor,
        active: false,
      };
    });
    update();
  }

  function update() {
    ticking = false;
    const vh = window.innerHeight;
    const scrollTop = scroller().scrollTop;
    for (const item of items) {
      const y = item.center - scrollTop;
      const near = y + item.half > -vh * 0.5 && y - item.half < vh * 1.5;
      if (near !== item.active) {
        // promote to a compositor layer only while the element can move on screen
        item.el.style.willChange = near ? 'transform' : '';
        item.active = near;
      }
      if (!near) continue;
      // -1 when the element's centre is one viewport below centre, +1 one viewport above
      const progress = Math.max(-1.5, Math.min(1.5, (vh / 2 - y) / vh));
      const ty = (progress * item.speed * item.unit).toFixed(1);
      const rot = item.rotate ? (progress * item.rotate).toFixed(2) : 0;
      if (ty === item.ty && rot === item.rot) continue;
      item.ty = ty;
      item.rot = rot;
      // transform is not inherited, so writing it directly avoids restyling the element's descendants
      item.el.style.transform = `translate3d(0, ${ty}px, 0)${rot ? ` rotate(${rot}deg)` : ''}`;
    }
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(update);
  }

  function init() {
    if (reduce.matches) return;
    document.documentElement.classList.add('fm-motion');
    reveal(document);
    steps(document);
    measure();
    // capture: the theme scrolls .page-wrapper on desktop and the window on mobile
    document.addEventListener('scroll', onScroll, { passive: true, capture: true });
    let pending;
    const remeasure = () => { cancelAnimationFrame(pending); pending = requestAnimationFrame(measure); };
    window.addEventListener('resize', remeasure, { passive: true });
    document.addEventListener('fm:slidechange', remeasure);
    document.fonts?.ready.then(remeasure);
    window.addEventListener('load', remeasure);
    setTimeout(remeasure, 1600); // after the hero entrance / first reveals settle
    if ('ResizeObserver' in window) new ResizeObserver(remeasure).observe(document.querySelector('#MainContent') || document.body);
    document.addEventListener('shopify:section:load', (event) => {
      reveal(event.target);
      steps(event.target);
      measure();
    });
  }

  return { init };
})();

/* Pointer tilt for tactile cards (fine pointers only). */
function fmInitTilt() {
  if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  document.addEventListener('pointermove', (event) => {
    const el = event.target.closest?.('[data-fm-tilt]');
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const max = parseFloat(el.dataset.fmTilt) || 4;
    const x = (event.clientX - rect.left) / rect.width - 0.5;
    const y = (event.clientY - rect.top) / rect.height - 0.5;
    el.style.setProperty('--tx', `${(-y * max).toFixed(2)}deg`);
    el.style.setProperty('--ty', `${(x * max).toFixed(2)}deg`);
  }, { passive: true });
  document.addEventListener('pointerout', (event) => {
    const el = event.target.closest?.('[data-fm-tilt]');
    if (el && !el.contains(event.relatedTarget)) {
      el.style.setProperty('--tx', '0deg');
      el.style.setProperty('--ty', '0deg');
    }
  }, { passive: true });
}

function fmInit() {
  FmMotion.init();
  fmInitTilt();
  fmInitHeader();
  fmInitReviewModal();
  FmCart.init();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', fmInit);
} else {
  fmInit();
}

if (!customElements.get('fm-carousel')) customElements.define('fm-carousel', FmCarousel);
if (!customElements.get('fm-video')) customElements.define('fm-video', FmVideo);
if (!customElements.get('fm-menu')) customElements.define('fm-menu', FmMenu);
